import React, { useEffect, useRef, useState } from "react";
import { translateBatch } from "./api";
import { useI18n } from "./i18n";

// In-memory cache shared across the whole app for this browser session, so
// switching between pages doesn't re-request translations already fetched.
const memoryCache = {};

function cacheKey(text, lang) {
  return `${lang}::${text}`;
}

/**
 * Translates one piece of admin-written text into the current language.
 * Returns the original text immediately (so nothing flashes blank), then
 * swaps in the translated version once it arrives. English is a no-op.
 */
export function useAutoTranslate(text) {
  const { lang } = useI18n();
  const [out, setOut] = useState(text);
  const requested = useRef(new Set());

  useEffect(() => {
    if (!text || lang === "en") {
      setOut(text);
      return;
    }
    const key = cacheKey(text, lang);
    if (memoryCache[key]) {
      setOut(memoryCache[key]);
      return;
    }
    setOut(text); // show original while translating
    if (requested.current.has(key)) return;
    requested.current.add(key);

    translateBatch([text], lang)
      .then((data) => {
        const translated = data?.translations?.[0] || text;
        memoryCache[key] = translated;
        setOut(translated);
      })
      .catch(() => setOut(text));
  }, [text, lang]);

  return out;
}

/**
 * Drop-in replacement for rendering a string of admin-written text.
 * Usage: <T>{tour.title}</T>  or  <T as="p" className="...">{settings.about_title}</T>
 */
export const T = ({ children, as: Tag = React.Fragment, ...props }) => {
  const translated = useAutoTranslate(typeof children === "string" ? children : "");
  if (Tag === React.Fragment) return <>{translated || children}</>;
  return <Tag {...props}>{translated || children}</Tag>;
};

/**
 * Translates a whole list of strings at once (fewer network round-trips than
 * translating each one individually) — useful for arrays like paragraphs
 * or FAQ lists. Returns the array unchanged for English.
 */
export function useAutoTranslateList(list) {
  const { lang } = useI18n();
  const [out, setOut] = useState(list || []);

  useEffect(() => {
    if (!list || list.length === 0 || lang === "en") {
      setOut(list || []);
      return;
    }
    const uncached = list.filter((t) => t && !memoryCache[cacheKey(t, lang)]);
    if (uncached.length === 0) {
      setOut(list.map((t) => (t ? memoryCache[cacheKey(t, lang)] || t : t)));
      return;
    }
    setOut(list);
    translateBatch(uncached, lang)
      .then((data) => {
        (data?.translations || []).forEach((tr, i) => {
          memoryCache[cacheKey(uncached[i], lang)] = tr;
        });
        setOut(list.map((t) => (t ? memoryCache[cacheKey(t, lang)] || t : t)));
      })
      .catch(() => setOut(list));
  }, [JSON.stringify(list), lang]);

  return out;
}
